/**
 * Validación y formato de campos del módulo ERP (2026-09-22, decisión Erwin):
 *  - RUT: se MUESTRA con guion (12345678-9) mientras se escribe y se GUARDA
 *    canónico (sin puntos ni guion, minúsculas) — regla del producto,
 *    espejo de dni.utils.js del backend. Se valida el dígito verificador.
 *  - Correo: formato básico.
 *  - Teléfono: 8 a 12 dígitos, `+` opcional al inicio; se guardan solo
 *    dígitos (con el `+` si venía). Cubre +56 9 1234 5678, 912345678, 221234567.
 *
 * Todos aceptan vacío como válido (los campos opcionales no se marcan); la
 * obligatoriedad la decide cada formulario.
 */

export const canonicalRut = (value) =>
  String(value || '').toLowerCase().replace(/[^0-9k]/g, '');

/** Dígito verificador módulo 11 del cuerpo numérico */
const computeDv = (body) => {
  let sum = 0;
  let mul = 2;
  for (let i = body.length - 1; i >= 0; i -= 1) {
    sum += Number(body[i]) * mul;
    mul = mul === 7 ? 2 : mul + 1;
  }
  const rest = 11 - (sum % 11);
  if (rest === 11) return '0';
  if (rest === 10) return 'k';
  return String(rest);
};

/** true si vacío o si el DV es correcto (cuerpo de 7 u 8 dígitos) */
export const isValidRut = (value) => {
  const clean = canonicalRut(value);
  if (!clean) return true;
  if (!/^\d{7,8}[0-9k]$/.test(clean)) return false;
  return computeDv(clean.slice(0, -1)) === clean.slice(-1);
};

/** Formato de pantalla mientras se escribe: 1234567-8 (sin puntos, para no pelear con el cursor) */
export const formatRutInput = (value) => {
  const clean = canonicalRut(value);
  if (clean.length <= 1) return clean.toUpperCase();
  return `${clean.slice(0, -1)}-${clean.slice(-1).toUpperCase()}`;
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export const isValidEmail = (value) => {
  const v = String(value || '').trim();
  return !v || EMAIL_RE.test(v);
};

/** Solo dígitos, conservando un `+` inicial */
export const normalizePhone = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const plus = raw.startsWith('+') ? '+' : '';
  return plus + raw.replace(/\D/g, '');
};

export const isValidPhone = (value) => {
  const v = normalizePhone(value);
  if (!v) return true;
  const digits = v.replace('+', '');
  return /^\d{8,12}$/.test(digits);
};

/** Mientras se escribe: deja solo dígitos, espacios y un + inicial */
export const formatPhoneInput = (value) => {
  const raw = String(value || '');
  const plus = raw.trim().startsWith('+') ? '+' : '';
  return plus + raw.replace(/[^\d\s]/g, '').replace(/\s{2,}/g, ' ');
};

/**
 * Revisa un objeto de formulario contra un mapa { 'path.al.campo': 'rut' | 'email' | 'phone' }.
 * Devuelve { 'path': 'rut' | 'email' | 'phone' } solo para los inválidos.
 */
export const collectFormatErrors = (form, fieldTypes) => {
  const errors = {};
  Object.entries(fieldTypes).forEach(([path, type]) => {
    const value = path.split('.').reduce((o, k) => (o ? o[k] : undefined), form);
    const ok = type === 'rut' ? isValidRut(value) : type === 'email' ? isValidEmail(value) : isValidPhone(value);
    if (!ok) errors[path] = type;
  });
  return errors;
};

/** Normaliza para guardar: RUT canónico, teléfono solo dígitos, correo trim/minúsculas */
export const normalizeForSave = (form, fieldTypes) => {
  const clone = JSON.parse(JSON.stringify(form));
  Object.entries(fieldTypes).forEach(([path, type]) => {
    const keys = path.split('.');
    const last = keys.pop();
    const parent = keys.reduce((o, k) => (o ? o[k] : undefined), clone);
    if (!parent || parent[last] === undefined || parent[last] === null) return;
    const v = parent[last];
    if (type === 'rut') parent[last] = canonicalRut(v);
    else if (type === 'phone') parent[last] = normalizePhone(v);
    else if (type === 'email') parent[last] = String(v).trim().toLowerCase();
  });
  return clone;
};
